import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Typography,
  Container,
  Grid,
  Chip
} from '@mui/material';
import {
  ArrowRight,
  Zap,
  Calendar,
  BarChart3,
  Users,
  Sparkles,
  Shield
} from 'lucide-react';

export default function HomePage() {
  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(to bottom right, #fef2f2, #fdf2f8)' }}>
      {/* Hero Section */}
      <Container maxWidth="md" sx={{ py: { xs: 8, md: 12 }, textAlign: 'center' }}>
        <Chip
          icon={<Sparkles className="w-4 h-4" />}
          label="AI-Powered Pinterest Automation"
          sx={{
            bgcolor: '#fee2e2',
            color: '#b91c1c',
            fontWeight: 500,
            fontSize: '1rem',
            mb: 2,
            px: 2,
            py: 2
          }}
        />
        <Typography variant="h2" sx={{ fontWeight: 700, mb: 3, color: 'text.primary', fontSize: { xs: '2.5rem', md: '3rem' } }}>
          Automate Your Pinterest Success with <Box component="span" sx={{ color: 'primary.main', display: 'inline' }}>Bean</Box>
        </Typography>
        <Typography variant="h6" sx={{ color: 'text.secondary', mb: 5, maxWidth: 700, mx: 'auto', lineHeight: 1.7 }}>
          The centralized platform that helps creators and businesses scale their Pinterest presence through AI-powered content creation, smart scheduling, and performance tracking.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, justifyContent: 'center', mb: 2 }}>
          <Button
            component={RouterLink}
            to="/signup"
            size="large"
            variant="contained"
            sx={{ bgcolor: 'primary.main', color: 'white', px: 5, py: 2, fontWeight: 600, '&:hover': { bgcolor: 'primary.dark' } }}
            endIcon={<ArrowRight className="w-4 h-4" />}
          >
            Get Started Free
          </Button>
          <Button
            component={RouterLink}
            to="/about"
            size="large"
            variant="outlined"
            sx={{ px: 5, py: 2, fontWeight: 600 }}
          >
            Learn More
          </Button>
        </Box>
      </Container>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
            Everything You Need to Dominate Pinterest
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 600, mx: 'auto' }}>
            Bean combines the power of AI with Pinterest best practices to help you create, schedule, and optimize your content for maximum engagement.
          </Typography>
        </Box>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6} lg={4}>
            <Card elevation={3} sx={{ border: 0, transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 8 } }}>
              <CardHeader
                avatar={<Box className="w-12 h-12" sx={{ bgcolor: '#fee2e2', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}><Zap className="w-6 h-6" style={{ color: '#dc2626' }} /></Box>}
                title={<Typography variant="h6" component="h3" fontWeight={600}>
                      AI Content Generation
                    </Typography>
                  }
                subheader={<Typography variant="body2">Create stunning Pinterest-optimized visuals and descriptions using advanced AI technology</Typography>}
              />
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <Card elevation={3} sx={{ border: 0, transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 8 } }}>
              <CardHeader
                avatar={<Box className="w-12 h-12" sx={{ bgcolor: '#dbeafe', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}><Calendar className="w-6 h-6" style={{ color: '#2563eb' }} /></Box>}
                title={<Typography variant="h6" component="h3" fontWeight={600}>
                      Smart Scheduling
                    </Typography>
                  }
                subheader={<Typography variant="body2">Schedule posts at optimal times based on your audience's activity patterns</Typography>}
              />
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <Card elevation={3} sx={{ border: 0, transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 8 } }}>
              <CardHeader
                avatar={<Box className="w-12 h-12" sx={{ bgcolor: '#dcfce7', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}><BarChart3 className="w-6 h-6" style={{ color: '#16a34a' }} /></Box>}
                title={<Typography variant="h6" component="h3" fontWeight={600}>
                      Performance Analytics
                    </Typography>
                  }
                subheader={<Typography variant="body2">Track engagement, reach, and conversion metrics to optimize your Pinterest strategy</Typography>}
              />
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <Card elevation={3} sx={{ border: 0, transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 8 } }}>
              <CardHeader
                avatar={<Box className="w-12 h-12" sx={{ bgcolor: '#ede9fe', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}><Users className="w-6 h-6" style={{ color: '#7c3aed' }} /></Box>}
                title={<Typography variant="h6" component="h3" fontWeight={600}>
                      Multi-Account Management
                    </Typography>
                  }
                subheader={<Typography variant="body2">Manage multiple Pinterest Business accounts from one centralized dashboard</Typography>}
              />
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <Card elevation={3} sx={{ border: 0, transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 8 } }}>
              <CardHeader
                avatar={<Box className="w-12 h-12" sx={{ bgcolor: '#ffedd5', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}><Shield className="w-6 h-6" style={{ color: '#ea580c' }} /></Box>}
                title={<Typography variant="h6" component="h3" fontWeight={600}>
                      Pinterest Compliance
                    </Typography>
                  }
                subheader={<Typography variant="body2">Stay compliant with Pinterest's guidelines and best practices automatically</Typography>}
              />
            </Card>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <Card elevation={3} sx={{ border: 0, transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 8 } }}>
              <CardHeader
                avatar={<Box className="w-12 h-12" sx={{ bgcolor: '#fce7f3', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}><Sparkles className="w-6 h-6" style={{ color: '#db2777' }} /></Box>}
                title={<Typography variant="h6" component="h3" fontWeight={600}>
                      Brand Consistency
                    </Typography>
                  }
                subheader={<Typography variant="body2">Maintain consistent branding across all your Pinterest content and boards</Typography>}
              />
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* CTA Section */}
      <Container maxWidth="md" sx={{ py: { xs: 8, md: 12 } }}>
        <Card sx={{ background: 'linear-gradient(to right, #dc2626, #db2777)', border: 0, color: 'white' }}>
          <CardContent sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
              Ready to Transform Your Pinterest Strategy?
            </Typography>
            <Typography variant="body1" sx={{ color: '#fee2e2', mb: 4, maxWidth: 600, mx: 'auto' }}>
              Join thousands of creators and businesses who are already using Bean to automate their Pinterest success and drive more traffic to their content.
            </Typography>
            <Button
              component={RouterLink}
              to="/signup"
              size="large"
              variant="contained"
              sx={{ bgcolor: 'white', color: 'primary.main', fontWeight: 700, px: 5, py: 2, '&:hover': { bgcolor: '#f3f4f6' } }}
              endIcon={<ArrowRight className="w-4 h-4" />}
            >
              Start Your Free Trial
            </Button>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
